import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Valor } from '../../components/Valor';
import { api, type DiaProjetado } from '../../lib/api';
import { dateBR, money } from '../../lib/format';

/**
 * Para onde o saldo vai.
 *
 * O resto do painel responde o que ja aconteceu. Esta e a pergunta que fica
 * de pe depois: no dia 10 eu tenho dinheiro para a parcela? A resposta so
 * serve se vier antes do dia 10 — depois dela o que restava fazer, que era
 * adiantar um recebimento ou empurrar uma conta, ja nao da mais.
 *
 * Por isso o cartao comeca pela frase, e nao pelo desenho: quem abre precisa
 * saber em UM segundo se tem problema. A curva vem depois, para quem quer ver
 * onde.
 *
 * Nada aqui e estimativa propria: cada ponto e saldo real mais vencimento que
 * o sistema ja projetou. Se um numero esta errado na curva, ele esta errado
 * tambem em Compromissos — e e la que se conserta.
 */

const JANELAS = [30, 60, 90] as const;

export function Projecao() {
  const [janela, setJanela] = useState<(typeof JANELAS)[number]>(90);
  const consulta = useQuery({ queryKey: ['projecao'], queryFn: () => api.projecao() });

  const dados = consulta.data;
  if (!dados) return null;

  // Uma so consulta traz 90 dias e a tela recorta. Trocar de janela nao volta
  // ao servidor — e a mesma curva, vista de mais perto.
  const dias = dados.dias.slice(0, janela + 1);
  const recorte = recortar(dias);

  return (
    <section className="card">
      <div className="between">
        <h2 className="card-title" style={{ marginBottom: 0 }}>
          Para onde o saldo vai
        </h2>
        <div className="row" role="group" aria-label="Janela da projecao">
          {JANELAS.map((dias) => (
            <button
              key={dias}
              className={`btn ghost ${janela === dias ? 'ativo' : ''}`.trim()}
              aria-pressed={janela === dias}
              onClick={() => setJanela(dias)}
            >
              {dias} dias
            </button>
          ))}
        </div>
      </div>

      <Veredito recorte={recorte} />

      <Curva dias={dias} />

      <dl className="projecao-numeros">
        <div>
          <dt>Hoje</dt>
          <dd>
            <Valor cents={dados.saldoHojeCents} />
          </dd>
        </div>
        <div>
          <dt>Entra</dt>
          <dd>
            <Valor cents={recorte.entradaCents.toString()} />
          </dd>
        </div>
        <div>
          <dt>Sai</dt>
          <dd>
            <Valor cents={recorte.saidaCents.toString()} saida />
          </dd>
        </div>
        <div>
          <dt>Em {dateBR(recorte.fim.date)}</dt>
          <dd>
            <Valor cents={recorte.fim.saldoCents} />
          </dd>
        </div>
      </dl>

      {/*
        As duas incertezas da curva, ditas em voz alta. Uma projecao que
        esconde o proprio erro convence mais e ajuda menos: quem confia nela
        sem saber onde ela e fraca nao tem como conferir nada.
      */}
      {BigInt(dados.atrasadoCents) > 0n && (
        <p className="card-hint projecao-ressalva">
          Somei <Valor cents={dados.atrasadoCents} saida /> em {dados.atrasados.length}{' '}
          vencimento(s) que ja passaram e seguem sem baixa, como se saissem hoje. Se algum ja foi
          pago, a baixa em Compromissos tira esse peso da curva.
        </p>
      )}

      {BigInt(dados.faturaAbertaCents) > 0n && (
        <p className="card-hint projecao-ressalva">
          Inclui <Valor cents={dados.faturaAbertaCents} saida /> de fatura de cartao. A que ainda
          nao fechou vai crescer ate o fechamento — aqui esta o valor de agora.
        </p>
      )}
    </section>
  );
}

interface Recorte {
  entradaCents: bigint;
  saidaCents: bigint;
  fim: DiaProjetado;
  menor: DiaProjetado;
  primeiroNegativo: DiaProjetado | null;
}

/**
 * Os numeros da janela escolhida.
 *
 * Recalculados no recorte, e nao herdados do servidor: o servidor respondeu
 * sobre 90 dias, e quem esta olhando 30 quer o pior dia DESSES 30. Reaproveitar
 * o total de 90 mostraria um fundo de poco que nao esta no desenho.
 */
function recortar(dias: DiaProjetado[]): Recorte {
  let entradaCents = 0n;
  let saidaCents = 0n;
  let menor = dias[0]!;
  let primeiroNegativo: DiaProjetado | null = null;

  for (const dia of dias) {
    entradaCents += BigInt(dia.entradaCents);
    saidaCents += BigInt(dia.saidaCents);
    if (BigInt(dia.saldoCents) < BigInt(menor.saldoCents)) menor = dia;
    if (primeiroNegativo === null && BigInt(dia.saldoCents) < 0n) primeiroNegativo = dia;
  }

  return { entradaCents, saidaCents, fim: dias[dias.length - 1]!, menor, primeiroNegativo };
}

/** A resposta em uma frase, antes de qualquer desenho. */
function Veredito({ recorte }: { recorte: Recorte }) {
  if (recorte.primeiroNegativo) {
    const dia = recorte.primeiroNegativo;
    return (
      <p className="projecao-veredito ruim">
        O saldo fica negativo em <strong>{dateBR(dia.date)}</strong>, em {money(dia.saldoCents)}.
        Faltam {diasAte(dia.date)} dia(s).
      </p>
    );
  }

  return (
    <p className="projecao-veredito bom">
      O saldo nao cruza o zero. O ponto mais baixo e{' '}
      <strong>{money(recorte.menor.saldoCents)}</strong>, em {dateBR(recorte.menor.date)}.
    </p>
  );
}

/**
 * A curva.
 *
 * SVG na mao em vez de biblioteca de grafico: sao duas linhas e uma area, e
 * trazer um pacote inteiro para isso custaria mais no carregamento do que o
 * desenho vale. O `viewBox` faz a largura acompanhar o cartao.
 *
 * A linha do zero so aparece quando ha risco de cruzar. Desenhar sempre uma
 * referencia que o saldo nunca chega perto ensina o olho a ignora-la.
 */
function Curva({ dias }: { dias: DiaProjetado[] }) {
  const LARGURA = 600;
  const ALTURA = 140;

  const saldos = dias.map((dia) => Number(BigInt(dia.saldoCents)));
  const maior = Math.max(...saldos, 0);
  const menor = Math.min(...saldos, 0);
  // Uma faixa de altura zero (saldo constante) dividiria por zero adiante.
  const faixa = maior - menor || 1;

  const x = (indice: number) => (indice / (dias.length - 1 || 1)) * LARGURA;
  const y = (valor: number) => ALTURA - ((valor - menor) / faixa) * ALTURA;

  const linha = saldos.map((valor, indice) => `${x(indice)},${y(valor)}`).join(' ');
  const area = `0,${ALTURA} ${linha} ${LARGURA},${ALTURA}`;
  const cruzaOZero = menor < 0;

  return (
    <svg
      className="projecao-curva"
      viewBox={`0 0 ${LARGURA} ${ALTURA}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Saldo projetado de ${dateBR(dias[0]!.date)} a ${dateBR(
        dias[dias.length - 1]!.date,
      )}`}
    >
      <polygon className="projecao-area" points={area} />
      <polyline className="projecao-linha" points={linha} />
      {cruzaOZero && <line className="projecao-zero" x1={0} x2={LARGURA} y1={y(0)} y2={y(0)} />}
    </svg>
  );
}

function diasAte(date: string): number {
  const alvo = new Date(`${date}T00:00:00`);
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return Math.round((alvo.getTime() - hoje.getTime()) / 86_400_000);
}
